import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.ts';
import { ScoreBadge } from '../components/ScoreBadge.tsx';
import { ClassificationBadge } from '../components/ClassificationBadge.tsx';
import type { Item } from '../lib/types.ts';

const STATUSES = ['', 'new', 'triaged', 'analysed', 'flagged', 'sent', 'dismissed', 'published'];
const CATEGORIES = ['', 'economic', 'business', 'property', 'finance', 'eu', 'grants', 'insolvency'];

function fmtDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: '2-digit' });
}

export function Archive() {
  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [scoreMin, setScoreMin] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.items.list({ status, category, score_min: scoreMin || undefined, limit: LIMIT, offset });
      setItems(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [status, category, scoreMin, offset]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Archive</h1>
        <span style={{ fontSize: 13, color: '#999' }}>{total} items</span>

        <select value={status} onChange={e => { setStatus(e.target.value); setOffset(0); }} style={{ fontSize: 12 }}>
          {STATUSES.map(s => <option key={s} value={s}>{s || 'All statuses'}</option>)}
        </select>

        <select value={category} onChange={e => { setCategory(e.target.value); setOffset(0); }} style={{ fontSize: 12 }}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c || 'All categories'}</option>)}
        </select>

        <label style={{ fontSize: 12 }}>
          Min score:&nbsp;
          <input type="number" min="0" max="10" value={scoreMin}
            onChange={e => { setScoreMin(Number(e.target.value)); setOffset(0); }}
            style={{ width: 40, fontSize: 12 }} />
        </label>

        <button onClick={load} style={{ fontSize: 12, padding: '4px 10px' }}>Search</button>
      </div>

      {loading && <p style={{ color: '#999' }}>Loading…</p>}

      {!loading && (
        <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 6, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={th}>Date</th>
                <th style={th}>Source</th>
                <th style={th}>Title</th>
                <th style={th}>Score</th>
                <th style={th}>Type</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={td}>{fmtDate(item.ingested_at)}</td>
                  <td style={td}><span style={{ fontSize: 11, color: '#999' }}>{item.source_id.replace(/_/g, ' ')}</span></td>
                  <td style={td} title={item.title ?? ''}>
                    <div style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.title ?? '—'}
                    </div>
                  </td>
                  <td style={td}><ScoreBadge score={item.deep_final_score} /></td>
                  <td style={td}><ClassificationBadge classification={item.deep_final_classification ?? null} /></td>
                  <td style={td}><span style={{ fontSize: 11, color: '#777' }}>{item.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>

          {total > LIMIT && (
            <div style={{ padding: '12px 16px', display: 'flex', gap: 8 }}>
              <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - LIMIT))}>← Prev</button>
              <span style={{ fontSize: 12, color: '#666', alignSelf: 'center' }}>
                {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
              </span>
              <button disabled={offset + LIMIT >= total} onClick={() => setOffset(o => o + LIMIT)}>Next →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#555' };
const td: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'top' };
